// client/src/contexts/PermissionsContext.tsx

import React, { createContext, useContext, useMemo, useCallback, useState, useEffect } from 'react';
import { QueryClientContext } from '@tanstack/react-query';
import { queryClient as defaultQueryClient } from '@/lib/queryClient';
import { 
  hasPermission as checkPermission, 
  canAccessResource, 
  DEFAULT_ROLE_PERMISSIONS,
  isValidRole,
  type Role,
  type Resource,
  type Action,
  type Permission,
} from '@shared/types/permissions';

// ============================================================================
// Types
// ============================================================================

interface PermissionsState {
  permissions: Permission[];
  role: Role | null;
  isLoading: boolean;
  error: string | null;
}

interface PermissionsContextValue extends PermissionsState {
  // Core permission checks
  hasPermission: (resource: Resource, action: Action) => boolean;
  hasAnyPermission: (checks: Array<{ resource: Resource; action: Action }>) => boolean;
  hasAllPermissions: (checks: Array<{ resource: Resource; action: Action }>) => boolean;
  
  // Resource access checks
  canAccess: (resource: Resource) => boolean;
  canAccessAny: (resources: Resource[]) => boolean;
  canAccessAll: (resources: Resource[]) => boolean;
  
  // Role checks (para retrocompatibilidad durante migración)
  isRole: (role: Role) => boolean;
  isAnyRole: (roles: Role[]) => boolean;
  
  // CRUD shortcuts
  canCreate: (resource: Resource) => boolean;
  canRead: (resource: Resource) => boolean;
  canUpdate: (resource: Resource) => boolean;
  canDelete: (resource: Resource) => boolean;
  canManage: (resource: Resource) => boolean;
  
  // Utility
  getPermissionsForResource: (resource: Resource) => Action[];

  /**
   * Re-fetches the current user.  Permissions are derived from
   * `GET /api/auth/user` (PM-58) and arrive via `useAuth`, so a refresh is
   * just a refetch of that query — no separate endpoint.
   */
  refreshPermissions: () => Promise<void>;
}

const PermissionsContext = createContext<PermissionsContextValue | undefined>(undefined);

// ============================================================================
// Provider Props
// ============================================================================

interface PermissionsProviderProps {
  children: React.ReactNode;
  /**
   * Rol del usuario actual (de useAuth)
   * Se usa para obtener permisos default si `permissions` no viene del BE.
   */
  role: Role | null;
  /**
   * Permisos del usuario (de useAuth → `GET /api/auth/user`, PM-58).
   * Si no se proveen, se usan los defaults del rol.
   */
  permissions?: Permission[];
  /**
   * Override para testing o casos especiales
   */
  overridePermissions?: Permission[];
}

// ============================================================================
// Provider Implementation
// ============================================================================

export function PermissionsProvider({ 
  children, 
  role,
  permissions: externalPermissions,
  overridePermissions 
}: PermissionsProviderProps) {
  // [SA-33 / 868kbjw74 EX-03] Pull the QueryClient so refreshPermissions()
  // can invalidate the /api/auth/user query and force useAuth to refetch
  // a fresh permission snapshot. In production App.tsx wraps everything in
  // <QueryClientProvider client={defaultQueryClient}>, which means the
  // context value is the same instance as `defaultQueryClient` — so falling
  // back to the singleton when no provider is present (e.g. in some unit
  // tests that drive PermissionsProvider in isolation) preserves behaviour
  // without forcing every test to wrap in QueryClientProvider.
  const reactQueryClient = useContext(QueryClientContext);
  const queryClient = reactQueryClient ?? defaultQueryClient;

  const [state, setState] = useState<PermissionsState>({
    permissions: [],
    role: null,
    isLoading: true,
    error: null,
  });

  // Determinar permisos efectivos
  const effectivePermissions = useMemo(() => {
    // Override tiene prioridad (para testing)
    if (overridePermissions) {
      return overridePermissions;
    }
    
    // Permisos externos (de useAuth o futuro /api/auth/me)
    if (externalPermissions && externalPermissions.length > 0) {
      return externalPermissions;
    }
    
    // Fallback: permisos default del rol
    if (role && isValidRole(role)) {
      return DEFAULT_ROLE_PERMISSIONS[role];
    }
    
    return [];
  }, [role, externalPermissions, overridePermissions]);

  // Actualizar estado cuando cambien las props
  useEffect(() => {
    setState({
      permissions: effectivePermissions,
      role,
      isLoading: false,
      error: null,
    });
  }, [effectivePermissions, role]);

  // ============================================================================
  // Permission Check Functions
  // ============================================================================

  /**
   * Verifica si el usuario tiene un permiso específico
   */
  const hasPermission = useCallback(
    (resource: Resource, action: Action): boolean => {
      return checkPermission(effectivePermissions, resource, action);
    },
    [effectivePermissions]
  );

  /**
   * Verifica si el usuario tiene AL MENOS UNO de los permisos especificados
   */
  const hasAnyPermission = useCallback(
    (checks: Array<{ resource: Resource; action: Action }>): boolean => {
      return checks.some(({ resource, action }) => 
        checkPermission(effectivePermissions, resource, action)
      );
    },
    [effectivePermissions]
  );

  /**
   * Verifica si el usuario tiene TODOS los permisos especificados
   */
  const hasAllPermissions = useCallback(
    (checks: Array<{ resource: Resource; action: Action }>): boolean => {
      return checks.every(({ resource, action }) => 
        checkPermission(effectivePermissions, resource, action)
      );
    },
    [effectivePermissions]
  );

  // ============================================================================
  // Resource Access Functions
  // ============================================================================

  /**
   * Verifica si el usuario puede acceder a un recurso (cualquier acción)
   */
  const canAccess = useCallback(
    (resource: Resource): boolean => {
      return canAccessResource(effectivePermissions, resource);
    },
    [effectivePermissions]
  );

  /**
   * Verifica si el usuario puede acceder a AL MENOS UNO de los recursos
   */
  const canAccessAny = useCallback(
    (resources: Resource[]): boolean => {
      return resources.some(resource => 
        canAccessResource(effectivePermissions, resource)
      );
    },
    [effectivePermissions]
  );

  /**
   * Verifica si el usuario puede acceder a TODOS los recursos
   */
  const canAccessAll = useCallback(
    (resources: Resource[]): boolean => {
      return resources.every(resource => 
        canAccessResource(effectivePermissions, resource)
      );
    },
    [effectivePermissions]
  );

  // ============================================================================
  // Role Check Functions (para retrocompatibilidad)
  // ============================================================================

  /**
   * Verifica si el usuario tiene un rol específico
   * @deprecated Usar hasPermission() en su lugar cuando sea posible
   */
  const isRole = useCallback(
    (checkRole: Role): boolean => role === checkRole,
    [role]
  );

  /**
   * Verifica si el usuario tiene alguno de los roles especificados
   * @deprecated Usar hasAnyPermission() en su lugar cuando sea posible
   */
  const isAnyRole = useCallback(
    (roles: Role[]): boolean => role !== null && roles.includes(role),
    [role]
  );

  // ============================================================================
  // CRUD Shortcuts
  // ============================================================================

  const canCreate = useCallback(
    (resource: Resource): boolean => hasPermission(resource, 'create'),
    [hasPermission]
  );

  const canRead = useCallback(
    (resource: Resource): boolean => hasPermission(resource, 'read'),
    [hasPermission]
  );

  const canUpdate = useCallback(
    (resource: Resource): boolean => hasPermission(resource, 'update'),
    [hasPermission]
  );

  const canDelete = useCallback(
    (resource: Resource): boolean => hasPermission(resource, 'delete'),
    [hasPermission]
  );

  const canManage = useCallback(
    (resource: Resource): boolean => hasPermission(resource, 'manage'),
    [hasPermission]
  );

  // ============================================================================
  // Utility Functions
  // ============================================================================

  /**
   * Obtiene las acciones permitidas para un recurso específico
   */
  const getPermissionsForResource = useCallback(
    (resource: Resource): Action[] => {
      const permission = effectivePermissions.find(p => p.resource === resource);
      return permission?.actions ?? [];
    },
    [effectivePermissions]
  );

  /**
   * [SA-33 / 868kbjw74 EX-03] Force a refetch of the /api/auth/user
   * query so a freshly revoked permission (e.g. userManagement) shows
   * up in the sidebar on the next render. The auth query also refetches
   * every 60s via refetchInterval, so callers can rely on the cache
   * being reasonably fresh without calling this directly. Most callers
   * (e.g. mutation hooks that grant/revoke permissions) should still
   * invoke `refreshPermissions()` after a successful mutation to skip
   * the wait.
   */
  const refreshPermissions = useCallback(async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] });
  }, [queryClient]);

  // ============================================================================
  // Context Value
  // ============================================================================

  const value: PermissionsContextValue = useMemo(
    () => ({
      // State
      permissions: effectivePermissions,
      role,
      isLoading: state.isLoading,
      error: state.error,
      
      // Permission checks
      hasPermission,
      hasAnyPermission,
      hasAllPermissions,
      
      // Resource access
      canAccess,
      canAccessAny,
      canAccessAll,
      
      // Role checks
      isRole,
      isAnyRole,
      
      // CRUD shortcuts
      canCreate,
      canRead,
      canUpdate,
      canDelete,
      canManage,
      
      // Utility
      getPermissionsForResource,
      refreshPermissions,
    }),
    [
      effectivePermissions,
      role,
      state.isLoading,
      state.error,
      hasPermission,
      hasAnyPermission,
      hasAllPermissions,
      canAccess,
      canAccessAny,
      canAccessAll,
      isRole,
      isAnyRole,
      canCreate,
      canRead,
      canUpdate,
      canDelete,
      canManage,
      getPermissionsForResource,
      refreshPermissions,
    ]
  );

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
}

// ============================================================================
// Hooks
// ============================================================================

/**
 * Hook principal para acceder al contexto de permisos
 * @throws Error si se usa fuera de PermissionsProvider
 */
export function usePermissions(): PermissionsContextValue {
  const context = useContext(PermissionsContext);
  if (context === undefined) {
    throw new Error('usePermissions must be used within a PermissionsProvider');
  }
  return context;
}

/**
 * Hook para verificar un permiso específico
 * @example
 * const canCreateProperty = useHasPermission('properties', 'create');
 */
export function useHasPermission(resource: Resource, action: Action): boolean {
  const { hasPermission } = usePermissions();
  return hasPermission(resource, action);
}

/**
 * Hook para verificar acceso a un recurso
 * @example
 * const canAccessReports = useCanAccess('reports');
 */
export function useCanAccess(resource: Resource): boolean {
  const { canAccess } = usePermissions();
  return canAccess(resource);
}

/**
 * Hook para obtener el rol actual
 * @example
 * const role = useCurrentRole();
 */
export function useCurrentRole(): Role | null {
  const { role } = usePermissions();
  return role;
}

/**
 * Hook para verificar si el usuario tiene un rol específico
 * @deprecated Usar useHasPermission cuando sea posible
 * @example
 * const isPM = useIsRole('property_manager');
 */
export function useIsRole(checkRole: Role): boolean {
  const { isRole } = usePermissions();
  return isRole(checkRole);
}

/**
 * Hook para obtener shortcuts CRUD de un recurso
 * @example
 * const { canCreate, canRead, canUpdate, canDelete } = useResourcePermissions('properties');
 */
export function useResourcePermissions(resource: Resource) {
  const { canCreate, canRead, canUpdate, canDelete, canManage, getPermissionsForResource } = usePermissions();
  
  return useMemo(() => ({
    canCreate: canCreate(resource),
    canRead: canRead(resource),
    canUpdate: canUpdate(resource),
    canDelete: canDelete(resource),
    canManage: canManage(resource),
    allowedActions: getPermissionsForResource(resource),
  }), [resource, canCreate, canRead, canUpdate, canDelete, canManage, getPermissionsForResource]);
}

// ============================================================================
// Re-exports para conveniencia
// ============================================================================

export type { Role, Resource, Action, Permission } from '@shared/types/permissions';
